import { useState } from "react";
import { PaperToggle, type PaperType } from "../../components/PaperToggle";
import type { ExamChoice } from "../../lib/catalog";
import { BackButton } from "../../components/BackButton";

export interface YearOption {
  year: number;
  questionCount: number;
  /** Downloaded — a green tick in the comps, and the word, not only the tick. */
  offline?: boolean;
}

interface YearPickScreenProps {
  subject: string;
  exam: ExamChoice;
  years: YearOption[];
  onPick: (year: number | "mixed", paper: PaperType) => void;
}

/**
 * Screen 16a — a bottom sheet over the hub.
 *
 * "Mixed" is recommended because a single year rehearses that year's quirks;
 * shuffled years is closer to what the exam actually asks.
 */
export function YearPickScreen({ subject, exam, years, onPick }: YearPickScreenProps) {
  const [paper, setPaper] = useState<PaperType>("objective");

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/practice" />

      <p className="m-0 pt-3 text-[12px] text-text-muted">{subject} · Past Questions</p>
      <h1 className="m-0 pt-1 font-[family-name:var(--font-display)] text-[20px] font-bold">
        Pick a paper and a year
      </h1>
      <p className="m-0 pt-1 text-[12.5px] text-text-muted">Tap a year to start.</p>

      <div className="pt-4">
        <PaperToggle value={paper} onChange={setPaper} hidden={exam === "JAMB"} />
      </div>

      {exam !== "JAMB" ? (
        <p className="m-0 pt-2.5 text-[11.5px] leading-[1.45] text-text-muted">
          {/* The answer is marked now, so promising a self-mark undersells it
              — and leaves a student surprised by a real score. */}
          Theory is WAEC's written paper — you write your answer and we mark it against the official
          scheme. JAMB is all objective.
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => onPick("mixed", paper)}
        className="mt-5 flex items-center justify-between rounded-[var(--radius-card)] border border-accent bg-accent-tint p-4 text-left"
      >
        <span className="text-[14px] font-semibold">Mixed — all years shuffled</span>
        <span className="rounded-[var(--radius-chip)] bg-surface px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em]">
          Recommended
        </span>
      </button>

      <ul className="m-0 flex list-none flex-col gap-2 p-0 pt-3">
        {years.map((option) => (
          <li key={option.year}>
            <button
              type="button"
              onClick={() => onPick(option.year, paper)}
              className="flex w-full items-center justify-between rounded-[var(--radius-control)] border border-border bg-surface px-3.5 py-3 text-left"
            >
              <span className="tabular text-[14px]">{option.year}</span>
              <span className="tabular flex items-center gap-1.5 text-[12px] text-text-muted">
                {option.offline ? (
                  <>
                    <span aria-hidden="true" className="text-positive">
                      ✓
                    </span>
                    offline
                  </>
                ) : (
                  `${option.questionCount} questions`
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
